import React, { useState, useEffect } from 'react';
import { X, User, Mail, Crown, Shield, Users, Eye, Trash2, Settings, Clock } from 'lucide-react';
import { teamWorkspaceService, TeamInvitation } from '../lib/teamWorkspaceService';
import { TeamMember, Team } from '../types';

interface TeamMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onMemberRemoved?: () => void;
}

export const TeamMembersModal: React.FC<TeamMembersModalProps> = ({
  isOpen,
  onClose,
  team,
  onMemberRemoved
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<string>('');

  useEffect(() => {
    if (isOpen && team) {
      loadMembers();
    }
  }, [isOpen, team]);

  const loadMembers = async () => {
    if (!team) return;

    try {
      setLoading(true);
      setError('');
      const teamMembers = await teamWorkspaceService.getTeamMembers(team.id);
      setMembers(teamMembers);
      // Pending invitations are owner-only; a non-owner call returns []. Don't
      // fail the whole modal if this errors.
      try {
        setInvitations(await teamWorkspaceService.getTeamInvitations(team.id));
      } catch {
        setInvitations([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Cancel this pending invitation?')) return;
    try {
      setError('');
      await teamWorkspaceService.cancelTeamInvitation(invitationId);
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const handleRoleChange = async (memberId: string, userId: string, currentRole: string) => {
    if (newRole === currentRole) {
      setEditingRole(null);
      return;
    }

    try {
      setError('');
      await teamWorkspaceService.updateMemberRole(team!.id, userId, newRole);
      await loadMembers();
      setEditingRole(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string, userId: string) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      setError('');
      await teamWorkspaceService.removeTeamMember(team!.id, userId);
      await loadMembers();
      if (onMemberRemoved) {
        onMemberRemoved();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4" />;
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'member': return <Users className="w-4 h-4" />;
      case 'viewer': return <Eye className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'member': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen || !team) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
            <p className="text-sm text-gray-600">{team.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading members...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No members found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((member) => {
                const memberEmail = (member as any).users?.email || 'Unknown';
                const isEditing = editingRole === member.id;
                
                return (
                  <div key={member.id} className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {memberEmail.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{memberEmail}</p>
                          <p className="text-xs text-gray-500">
                            {member.status === 'invited' ? 'Invited' : member.joined_at ? `Joined ${new Date(member.joined_at).toLocaleDateString()}` : 'Active'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {isEditing ? (
                          <div className="flex items-center space-x-2">
                            <select
                              value={newRole}
                              onChange={(e) => setNewRole(e.target.value)}
                              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                              <option value="viewer">Viewer</option>
                            </select>
                            <button
                              onClick={() => handleRoleChange(member.id, member.user_id, member.role)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingRole(null);
                                setNewRole('');
                              }}
                              className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-md transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getRoleColor(member.role)}`}>
                              {getRoleIcon(member.role)}
                              <span>{member.role}</span>
                            </div>
                            {member.role !== 'owner' && (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => {
                                    setEditingRole(member.id);
                                    setNewRole(member.role);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Change role"
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleRemoveMember(member.id, member.user_id)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Remove member"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pending invitations — emails invited that haven't joined yet
              (including people who don't have an account yet). */}
          {invitations.length > 0 && (
            <div className="mt-6">
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Clock className="h-4 w-4 text-amber-500" />
                Pending invitations
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  {invitations.length}
                </span>
              </h4>
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/60 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <Mail className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{inv.email}</p>
                        <p className="text-xs text-gray-500">
                          Invited as {inv.role} &middot; joins automatically when they sign up
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvitation(inv.id)}
                      className="p-1 text-gray-400 transition-colors hover:text-red-600"
                      title="Cancel invitation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

